import { MouseEvent } from 'react';

import {
  CSVFileIcon,
  ExcelFileIcon,
  GenericFileIcon,
  JPGFileIcon,
  PDFFileIcon,
  PNGFileIcon,
  WordDocFileIcon,
} from '../../../../components/Icon';
import { UploadedFile } from '../../../../components/FileUpload';
import { TextLink, TextLinkSize } from '../../../../components/TextLink';
import { normalizeTestId } from '../../../../utils';

import './FileReviewList.css';

interface FileReviewListProps {
  files?: UploadedFile[];
  sections?: FileReviewSection[];
  onFileClick?: (file: UploadedFile) => void;
  onEditClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

export interface FileReviewSection {
  files?: UploadedFile[];
  groups?: FileReviewSubsection[];
  title: string;
}

export interface FileReviewSubsection {
  files: UploadedFile[];
  title: string;
}

function getFileIcon(fileName: string) {
  const extension = fileName.split('.').pop()?.toLowerCase() || '';

  switch (extension) {
    case 'pdf':
      return (
        <PDFFileIcon
          ariaLabel={`PDF file: ${fileName}`}
          size={48}
        />
      );
    case 'doc':
    case 'docx':
      return (
        <WordDocFileIcon
          ariaLabel={`Word document: ${fileName}`}
          size={48}
        />
      );
    case 'xls':
    case 'xlsx':
      return (
        <ExcelFileIcon
          ariaLabel={`Excel file: ${fileName}`}
          size={48}
        />
      );
    case 'csv':
      return (
        <CSVFileIcon
          ariaLabel={`CSV file: ${fileName}`}
          size={48}
        />
      );
    case 'jpg':
    case 'jpeg':
      return (
        <JPGFileIcon
          ariaLabel={`JPEG image: ${fileName}`}
          size={48}
        />
      );
    case 'png':
      return (
        <PNGFileIcon
          ariaLabel={`PNG image: ${fileName}`}
          size={48}
        />
      );
    default:
      return (
        <GenericFileIcon
          ariaLabel={`File: ${fileName}`}
          size={48}
        />
      );
  }
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function FileReviewList({ files = [], sections, onFileClick, onEditClick }: FileReviewListProps) {
  const baseTestId = 'file-review-list';
  const fileSections = sections ?? [{ files, title: '' }];
  const hasFiles = fileSections.some(
    (section) => (section.files?.length ?? 0) > 0 || section.groups?.some((group) => group.files.length > 0),
  );

  if (!hasFiles) {
    return (
      <span
        className='file-review-list__empty'
        data-testid={`${baseTestId}-empty`}>
        No files uploaded
      </span>
    );
  }

  return (
    <div
      className='file-review-list'
      data-testid={baseTestId}>
      <ul
        aria-label='List of uploaded document sections'
        className='file-review-list__items'
        data-testid={`${baseTestId}-items`}>
        {fileSections.map((section) => {
          const sectionKey = section.title || 'files';
          const sectionTestId = normalizeTestId(`${baseTestId}-section-${sectionKey}`);
          const groups = section.groups ?? [{ files: section.files ?? [], title: '' }];

          return (
            <li
              key={sectionKey}
              className='file-review-list__item'
              data-testid={sectionTestId}>
              <div
                className='file-review-list__section-files'
                data-testid={`${sectionTestId}-files`}>
                {section.title && (
                  <span
                    className='file-review-list__section'
                    data-testid={`${sectionTestId}-title`}>
                    {section.title}
                  </span>
                )}
                {groups.map((group) => {
                  const groupKey = group.title || 'files';

                  return (
                    <div
                      key={groupKey}
                      className='file-review-list__subsection'>
                      {group.title && <span className='file-review-list__subsection-title'>{group.title}</span>}
                      <ul
                        aria-label={`${group.title || section.title || 'Uploaded files'} files`}
                        className='file-review-list__file-items'>
                        {group.files.map((file) => {
                          const fileKey = file.documentId || file.id || file.name;
                          const itemTestId = normalizeTestId(`${sectionTestId}-file-${fileKey}`);

                          return (
                            <li
                              key={fileKey}
                              className='file-review-list__file-item'
                              data-testid={itemTestId}>
                              <div
                                className='file-review-list__icon'
                                data-testid={`${itemTestId}-icon`}>
                                {getFileIcon(file.name)}
                              </div>
                              <div
                                className='file-review-list__info'
                                data-testid={`${itemTestId}-info`}>
                                {onFileClick && (!file.scanStatus || file.scanStatus === 'CLEAN') ? (
                                  <TextLink
                                    ariaLabel={`Open ${file.name} in a new tab`}
                                    className='file-review-list__name-link'
                                    data-testid={`${itemTestId}-name`}
                                    size={TextLinkSize.MEDIUM}
                                    text={file.name}
                                    onClick={() => onFileClick(file)}
                                  />
                                ) : (
                                  <span
                                    className='file-review-list__name'
                                    data-testid={`${itemTestId}-name`}>
                                    {file.name}
                                  </span>
                                )}
                                <span
                                  className='file-review-list__size'
                                  data-testid={`${itemTestId}-size`}>
                                  {formatFileSize(file.size)}
                                </span>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
              {onEditClick && (
                <a
                  aria-label={`Edit ${section.title || 'uploaded files'}`}
                  className='file-review-list__edit-link'
                  data-testid={`${sectionTestId}-edit`}
                  href='#'
                  onClick={onEditClick}>
                  Edit
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default FileReviewList;
